// src/context/ProfilePrefetchContext.jsx
/**
 * ProfilePrefetchContext
 * ----------------------
 * Global, session-scoped cache for user "profile/demographics" data.
 *
 * Patient-context support
 * -----------------------
 * All caches are now keyed by (uid + id) using `patientCacheKey(uid, id)`.
 * This prevents leaking profile/metadata across patients within the same clinician session.
 *
 * What is cached
 *  - Profile JSON: most recent metadata JSON object for a uid+id (downloaded content).
 *  - Profile info: newest metadata timestamp (and optionally filename) for a uid+id
 *    without downloading the JSON (fast gate checks).
 *
 * Goals
 *  - Prefetch on login (non-blocking) to reduce later in-flow latency.
 *  - Avoid redundant blocking fetches in assessment/questionnaire flows.
 *  - Provide a stable API for:
 *      • getCachedProfile / ensureProfile
 *      • getCachedProfileInfo / ensureProfileInfo
 *      • setProfile (updates caches after a save/confirm)
 *      • clearProfile / clearAllProfiles (hygiene on logout / uid change)
 *
 * Caching strategy
 *  - In-memory cache for fastest reads.
 *  - sessionStorage mirror to persist across reloads within the same tab.
 *  - TTL-based freshness checks.
 *
 * Notes
 *  - This does not replace the storage-of-record (Firebase Storage). It is an
 *    optimisation layer to reduce UX latency in "review/prefill" and gating flows.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchLatestMetadata } from "../utils/firebase/fetchLatestMetadata";
import { fetchLatestMetadataInfo } from "../utils/firebase/fetchLatestMetadataInfo";
import { patientCacheKey } from "../utils/patient/patientStoragePath";

/** Session storage keys (exported for reuse/debugging). */
export const SESSION_KEY_PREFIX = "pdx.profilePrefetch.v1";
export const SESSION_INFO_KEY_PREFIX = "pdx.profilePrefetch.info.v1";

/**
 * Default TTL (10 minutes) for cache freshness checks.
 * Exported so callers can standardise behaviour.
 */
export const DEFAULT_TTL_MS = 10 * 60 * 1000;

/**
 * @typedef {object} CachedProfile
 * @property {"idle"|"loading"|"ready"|"error"} status
 * @property {object|null} data
 * @property {string|null} error
 * @property {number|null} fetchedAtMs
 */

/**
 * @typedef {object} CachedProfileInfo
 * @property {"idle"|"loading"|"ready"|"error"} status
 * @property {number|null} latestMs
 * @property {string|null} newestName
 * @property {string|null} error
 * @property {number|null} fetchedAtMs
 */

/**
 * @typedef {object} ProfileEnsureArgs
 * @property {string} uid
 *   Firebase Auth UID whose metadata should be looked up.
 * @property {string|null} [id]
 *   Optional patient or participant ID used to isolate clinician-side caches.
 * @property {number} [ttlMs]
 *   Cache freshness window in milliseconds.
 * @property {boolean} [force=false]
 *   When `true`, bypasses TTL checks and fetches again.
 */

/**
 * @typedef {object} ProfilePrefetchContextValue
 * @property {Function} getCachedProfile
 *   Returns the best available cached metadata JSON for a given uid/id pair.
 * @property {Function} ensureProfile
 *   Ensures metadata JSON is cached and returns it, fetching from Storage if needed.
 * @property {Function} setProfile
 *   Writes profile JSON into both memory and sessionStorage caches.
 * @property {Function} getCachedProfileInfo
 *   Returns cached "latest metadata" summary information for a uid/id pair.
 * @property {Function} ensureProfileInfo
 *   Ensures the latest metadata summary is cached and returns it.
 * @property {Function} clearProfile
 *   Clears one cached uid/id pair from memory and sessionStorage.
 * @property {Function} clearAllProfiles
 *   Clears every cached profile/info entry for the current browser tab.
 */

const ProfilePrefetchContext = createContext(null);

/**
 * Returns true if a cached entry is within TTL.
 *
 * @param {object|null|undefined} entry
 * @param {number|null} [entry.fetchedAtMs]
 *   Timestamp of the last successful fetch.
 * @param {number} ttlMs
 * @returns {boolean}
 */
export function isEntryFresh(entry, ttlMs) {
  if (!entry?.fetchedAtMs) return false;
  return Date.now() - entry.fetchedAtMs <= ttlMs;
}

/**
 * React provider that supplies profile cache + fetch helpers.
 *
 * @param {object} props
 * @param {*} props.children
 * @returns {JSX.Element}
 */
export function ProfilePrefetchProvider({ children }) {
  // cacheByKey maps `patientCacheKey(uid, id)` to the cached JSON payload state.
  const [cacheByKey, setCacheByKey] = useState({});
  // infoByKey maps `patientCacheKey(uid, id)` to lightweight freshness metadata.
  const [infoByKey, setInfoByKey] = useState({});

  // Track in-flight requests (dedupe concurrent callers) by uid+patient key
  const inflightProfileRef = useRef({});
  const inflightInfoRef = useRef({});

  /* ------------------------------------------------------------------ */
  /* sessionStorage helpers                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Read a profile JSON cache entry from sessionStorage.
   *
   * @param {string} key patientCacheKey(uid, id)
   * @returns {CachedProfile|null}
   */
  const readSession = useCallback((key) => {
    try {
      const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}:${key}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  /**
   * Write a profile JSON cache entry to sessionStorage.
   *
   * @param {string} key patientCacheKey(uid, id)
   * @param {CachedProfile} value
   */
  const writeSession = useCallback((key, value) => {
    try {
      sessionStorage.setItem(
        `${SESSION_KEY_PREFIX}:${key}`,
        JSON.stringify(value),
      );
    } catch {
      // ignore quota / disabled storage
    }
  }, []);

  /**
   * Read a profile "info" cache entry from sessionStorage.
   *
   * @param {string} key patientCacheKey(uid, id)
   * @returns {CachedProfileInfo|null}
   */
  const readSessionInfo = useCallback((key) => {
    try {
      const raw = sessionStorage.getItem(`${SESSION_INFO_KEY_PREFIX}:${key}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  /**
   * Write a profile "info" cache entry to sessionStorage.
   *
   * @param {string} key patientCacheKey(uid, id)
   * @param {CachedProfileInfo} value
   */
  const writeSessionInfo = useCallback((key, value) => {
    try {
      sessionStorage.setItem(
        `${SESSION_INFO_KEY_PREFIX}:${key}`,
        JSON.stringify(value),
      );
    } catch {
      // ignore quota / disabled storage
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* getters                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Returns best available cached profile entry for uid+id
   * (memory first, then sessionStorage).
   *
   * @param {string} uid
   * @param {string|null|undefined} id
   * @returns {CachedProfile|null}
   */
  const getCachedProfile = useCallback(
    (uid, id) => {
      if (!uid) return null;
      const key = patientCacheKey(uid, id);
      if (cacheByKey[key]) return cacheByKey[key];
      return readSession(key);
    },
    [cacheByKey, readSession],
  );

  /**
   * Returns best available cached profile info entry for uid+id
   * (memory first, then sessionStorage).
   *
   * @param {string} uid
   * @param {string|null|undefined} id
   * @returns {CachedProfileInfo|null}
   */
  const getCachedProfileInfo = useCallback(
    (uid, id) => {
      if (!uid) return null;
      const key = patientCacheKey(uid, id);
      if (infoByKey[key]) return infoByKey[key];
      return readSessionInfo(key);
    },
    [infoByKey, readSessionInfo],
  );

  /* ------------------------------------------------------------------ */
  /* mutation / hygiene                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Writes profile JSON into caches (memory + sessionStorage) for uid+id.
   * Also updates "info" cache with a new "latestMs" (best-effort) because a save/confirm
   * implies a new artefact exists.
   *
   * @param {string} uid
   * @param {string|null|undefined} id
   * @param {object|null} data
   */
  const setProfile = useCallback(
    (uid, id, data) => {
      if (!uid) return;

      const key = patientCacheKey(uid, id);
      const now = Date.now();

      /** @type {CachedProfile} */
      const entry = {
        status: "ready",
        data: data ?? null,
        error: null,
        fetchedAtMs: now,
      };

      setCacheByKey((prev) => ({ ...prev, [key]: entry }));
      writeSession(key, entry);

      /** @type {CachedProfileInfo} */
      const infoEntry = {
        status: "ready",
        latestMs: now,
        newestName: null,
        error: null,
        fetchedAtMs: now,
      };

      setInfoByKey((prev) => ({ ...prev, [key]: infoEntry }));
      writeSessionInfo(key, infoEntry);
    },
    [writeSession, writeSessionInfo],
  );

  /**
   * Removes cached profile + info for a single uid+id (memory + sessionStorage).
   *
   * @param {string} uid
   * @param {string|null|undefined} id
   */
  const clearProfile = useCallback((uid, id) => {
    if (!uid) return;

    const key = patientCacheKey(uid, id);

    setCacheByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    setInfoByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    try {
      sessionStorage.removeItem(`${SESSION_KEY_PREFIX}:${key}`);
      sessionStorage.removeItem(`${SESSION_INFO_KEY_PREFIX}:${key}`);
    } catch {
      // ignore
    }
  }, []);

  /**
   * Clears ALL cached profile + info entries (memory + sessionStorage).
   * Use on logout, or when the active uid changes.
   */
  const clearAllProfiles = useCallback(() => {
    setCacheByKey({});
    setInfoByKey({});

    try {
      const prefixA = `${SESSION_KEY_PREFIX}:`;
      const prefixB = `${SESSION_INFO_KEY_PREFIX}:`;

      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (key.startsWith(prefixA) || key.startsWith(prefixB)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* ensure (fetch + dedupe)                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Ensures profile JSON is available for uid+id.
   *
   * Behaviour
   *  - If cached and fresh (TTL) and not forced, returns cached data immediately.
   *  - Otherwise fetches the latest metadata JSON from storage (deduped per uid+id),
   *    updates caches, and returns the object (or null).
   *
   * @param {ProfileEnsureArgs} args
   * @returns {Promise<object|null>}
   */
  const ensureProfile = useCallback(
    async ({ uid, id = "", ttlMs = DEFAULT_TTL_MS, force = false }) => {
      if (!uid) throw new Error("ensureProfile: uid is required");

      const key = patientCacheKey(uid, id);
      const existing = getCachedProfile(uid, id);

      if (
        !force &&
        existing?.status === "ready" &&
        isEntryFresh(existing, ttlMs)
      ) {
        return existing.data ?? null;
      }

      if (inflightProfileRef.current[key])
        return inflightProfileRef.current[key];

      // best-effort loading marker
      setCacheByKey((prev) => ({
        ...prev,
        [key]: {
          status: "loading",
          data: prev[key]?.data ?? null,
          error: null,
          fetchedAtMs: prev[key]?.fetchedAtMs ?? null,
        },
      }));

      inflightProfileRef.current[key] = (async () => {
        try {
          const data = await fetchLatestMetadata(uid, id);

          /** @type {CachedProfile} */
          const entry = {
            status: "ready",
            data: data ?? null,
            error: null,
            fetchedAtMs: Date.now(),
          };

          setCacheByKey((prev) => ({ ...prev, [key]: entry }));
          writeSession(key, entry);
          return data ?? null;
        } catch (e) {
          const msg = e?.message ? String(e.message) : "Unknown error";

          /** @type {CachedProfile} */
          const entry = {
            status: "error",
            data: null,
            error: msg,
            fetchedAtMs: Date.now(),
          };

          setCacheByKey((prev) => ({ ...prev, [key]: entry }));
          writeSession(key, entry);
          return null;
        } finally {
          delete inflightProfileRef.current[key];
        }
      })();

      return inflightProfileRef.current[key];
    },
    [getCachedProfile, writeSession],
  );

  /**
   * Ensures profile "info" (latest metadata timestamp) is available for uid+id.
   *
   * Behaviour
   *  - If cached and fresh (TTL) and not forced, returns cached entry.
   *  - Otherwise fetches newest timestamp/name from storage metadata (deduped per uid+id),
   *    updates caches, and returns the CachedProfileInfo entry.
   *
   * @param {ProfileEnsureArgs} args
   * @returns {Promise<CachedProfileInfo>}
   */
  const ensureProfileInfo = useCallback(
    async ({ uid, id = "", ttlMs = DEFAULT_TTL_MS, force = false }) => {
      if (!uid) throw new Error("ensureProfileInfo: uid is required");

      const key = patientCacheKey(uid, id);
      const existing = getCachedProfileInfo(uid, id);

      if (
        !force &&
        existing?.status === "ready" &&
        isEntryFresh(existing, ttlMs)
      ) {
        return existing;
      }

      if (inflightInfoRef.current[key]) return inflightInfoRef.current[key];

      // best-effort loading marker
      setInfoByKey((prev) => ({
        ...prev,
        [key]: {
          status: "loading",
          latestMs: prev[key]?.latestMs ?? null,
          newestName: prev[key]?.newestName ?? null,
          error: null,
          fetchedAtMs: prev[key]?.fetchedAtMs ?? null,
        },
      }));

      inflightInfoRef.current[key] = (async () => {
        try {
          const info = await fetchLatestMetadataInfo(uid, id);

          /** @type {CachedProfileInfo} */
          const entry = {
            status: "ready",
            latestMs: info?.latestMs ?? null,
            newestName: info?.newestName ?? null,
            error: null,
            fetchedAtMs: Date.now(),
          };

          setInfoByKey((prev) => ({ ...prev, [key]: entry }));
          writeSessionInfo(key, entry);
          return entry;
        } catch (e) {
          const msg = e?.message ? String(e.message) : "Unknown error";

          /** @type {CachedProfileInfo} */
          const entry = {
            status: "error",
            latestMs: null,
            newestName: null,
            error: msg,
            fetchedAtMs: Date.now(),
          };

          setInfoByKey((prev) => ({ ...prev, [key]: entry }));
          writeSessionInfo(key, entry);
          return entry;
        } finally {
          delete inflightInfoRef.current[key];
        }
      })();

      return inflightInfoRef.current[key];
    },
    [getCachedProfileInfo, writeSessionInfo],
  );

  /* ------------------------------------------------------------------ */
  /* context value                                                       */
  /* ------------------------------------------------------------------ */

  const value = useMemo(
    () => ({
      // profile JSON
      getCachedProfile,
      ensureProfile,
      setProfile,

      // profile info (timestamp)
      getCachedProfileInfo,
      ensureProfileInfo,

      // hygiene
      clearProfile,
      clearAllProfiles,
    }),
    [
      getCachedProfile,
      ensureProfile,
      setProfile,
      getCachedProfileInfo,
      ensureProfileInfo,
      clearProfile,
      clearAllProfiles,
    ],
  );

  return (
    <ProfilePrefetchContext.Provider value={value}>
      {children}
    </ProfilePrefetchContext.Provider>
  );
}

/**
 * Hook to consume the profile prefetch context.
 *
 * @returns {ProfilePrefetchContextValue}
 */
export function useProfilePrefetch() {
  const ctx = useContext(ProfilePrefetchContext);
  if (!ctx) {
    throw new Error(
      "useProfilePrefetch must be used within a ProfilePrefetchProvider",
    );
  }
  return ctx;
}
